/**
 * PlayerStatsPanel.js - Tournament Player Statistics Component
 * Displays leaderboards, career stats, and per-player detail view.
 * Embedded in ResultsPhase and accessible from tournament detail.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEADERBOARD_CATEGORIES = [
  { key: 'runs', label: 'Most Runs', icon: 'ph-bold ph-baseball', unit: 'runs', secondary: 'SR' },
  { key: 'wickets', label: 'Most Wickets', icon: 'ph-bold ph-target', unit: 'wkts', secondary: 'Econ' },
  { key: 'sixes', label: 'Most Sixes', icon: 'ph-bold ph-fire', unit: 'sixes', secondary: 'SR' },
  { key: 'strike_rate', label: 'Best Strike Rate', icon: 'ph-bold ph-lightning', unit: 'SR', secondary: 'Runs' },
  { key: 'economy', label: 'Best Economy', icon: 'ph-bold ph-chart-line-down', unit: 'Econ', secondary: 'Wkts' },
  { key: 'average', label: 'Best Average', icon: 'ph-bold ph-trend-up', unit: 'Avg', secondary: 'Runs' },
  { key: 'catches', label: 'Most Catches', icon: 'ph-bold ph-hand-grabbing', unit: 'catches', secondary: 'RO' },
  { key: 'maidens', label: 'Most Maidens', icon: 'ph-bold ph-shield-check', unit: 'maidens', secondary: 'Econ' },
];

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#3AAFA9] border-t-transparent"></div>
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-12 text-gray-400" data-testid="player-stats-empty">
      <i className="ph-bold ph-chart-bar text-4xl mb-3 block"></i>
      <p className="font-medium">{message}</p>
      <p className="text-sm mt-1">Stats will appear once matches are completed.</p>
    </div>
  );
}

function StatBadge({ value, label, color = 'gray' }) {
  const colorMap = {
    navy: 'bg-[#2B3A67]/10 text-[#2B3A67]',
    teal: 'bg-[#3AAFA9]/10 text-[#3AAFA9]',
    pink: 'bg-[#E54562]/10 text-[#E54562]',
    gold: 'bg-[#F4D341]/15 text-[#B8960C]',
    gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <div className={`${colorMap[color]} rounded-lg px-3 py-2 text-center`}>
      <p className="text-lg font-bold leading-tight">{value}</p>
      <p className="text-[10px] uppercase tracking-wider font-bold opacity-70">{label}</p>
    </div>
  );
}

// ── Leaderboard Card ──

function LeaderboardCard({ category, entries, onPlayerClick }) {
  if (!entries?.length) return null;

  const cat = LEADERBOARD_CATEGORIES.find(c => c.key === category) || {};

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`leaderboard-${category}`}>
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <i className={`${cat.icon} text-[#2B3A67]`}></i>
        <h3 className="font-bold text-sm">{cat.label}</h3>
      </div>
      <div className="divide-y divide-gray-50">
        {entries.map((entry, idx) => (
          <button
            key={entry.player_pool_id}
            onClick={() => onPlayerClick?.(entry.player_pool_id)}
            className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
            data-testid={`leaderboard-entry-${category}-${idx}`}
          >
            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
              idx === 0 ? 'bg-[#F4D341] text-[#2B3A67]' :
              idx === 1 ? 'bg-gray-300 text-gray-700' :
              idx === 2 ? 'bg-amber-600 text-white' :
              'bg-gray-100 text-gray-500'
            }`}>
              {entry.rank}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{entry.player_name}</p>
              <p className="text-[11px] text-gray-400">{entry.team_name || 'N/A'} &middot; {entry.matches}M</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-sm text-[#2B3A67]">{formatValue(entry.value, category)}</p>
              {entry.secondary_value != null && (
                <p className="text-[10px] text-gray-400">{cat.secondary}: {formatValue(entry.secondary_value, cat.secondary)}</p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatValue(val, category) {
  if (val == null) return '-';
  if (typeof val === 'number') {
    if (['strike_rate', 'economy', 'average', 'bowling_average', 'SR', 'Econ', 'Avg'].includes(category)) {
      return val.toFixed(2);
    }
    return val.toString();
  }
  return val;
}


// ── Player Detail Card ──

function PlayerDetailCard({ career, matches, onClose }) {
  const [activeTab, setActiveTab] = useState('batting');

  if (!career) return null;

  const tabs = [
    { key: 'batting', label: 'Batting', icon: 'ph-bold ph-baseball' },
    { key: 'bowling', label: 'Bowling', icon: 'ph-bold ph-target' },
    { key: 'fielding', label: 'Fielding', icon: 'ph-bold ph-hand-grabbing' },
    { key: 'matches', label: 'Matches', icon: 'ph-bold ph-list-numbers' },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden" data-testid="player-detail-card">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2B3A67] to-[#3AAFA9] p-5 text-white relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white" data-testid="player-detail-close">
          <i className="ph-bold ph-x text-lg"></i>
        </button>
        <h2 className="text-xl font-bold">{career.player_name}</h2>
        <p className="text-sm text-white/70 mt-0.5">{career.team_name || 'Unknown Team'}</p>
        <div className="flex gap-4 mt-3">
          <div><span className="text-2xl font-bold">{career.matches_played}</span><span className="text-xs ml-1 text-white/60">Matches</span></div>
          {career.potm_count > 0 && (
            <div><span className="text-2xl font-bold">{career.potm_count}</span><span className="text-xs ml-1 text-white/60">POTM</span></div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 px-4 py-2.5 text-xs font-bold flex items-center justify-center gap-1.5 transition-colors whitespace-nowrap ${
              activeTab === t.key ? 'border-b-2 border-[#3AAFA9] text-[#2B3A67]' : 'text-gray-400 hover:text-gray-600'
            }`}
            data-testid={`player-detail-tab-${t.key}`}
          >
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-5">
        {activeTab === 'batting' && <BattingStats career={career} />}
        {activeTab === 'bowling' && <BowlingStats career={career} />}
        {activeTab === 'fielding' && <FieldingStats career={career} />}
        {activeTab === 'matches' && <MatchBreakdown matches={matches} />}
      </div>
    </div>
  );
}

function BattingStats({ career }) {
  const c = career;
  const hs = c.is_highest_not_out ? `${c.highest_score}*` : `${c.highest_score}`;
  return (
    <div className="space-y-4" data-testid="player-batting-stats">
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.innings_batted} label="Inn" color="gray" />
        <StatBadge value={c.total_runs} label="Runs" color="navy" />
        <StatBadge value={hs} label="HS" color="teal" />
        <StatBadge value={c.batting_average.toFixed(1)} label="Avg" color="pink" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.batting_strike_rate.toFixed(1)} label="SR" color="gold" />
        <StatBadge value={c.total_fours} label="4s" color="gray" />
        <StatBadge value={c.total_sixes} label="6s" color="gray" />
        <StatBadge value={c.not_outs} label="NO" color="gray" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.hundreds} label="100s" color="navy" />
        <StatBadge value={c.fifties} label="50s" color="teal" />
        <StatBadge value={c.thirties} label="30s" color="gray" />
        <StatBadge value={c.ducks} label="Ducks" color="pink" />
      </div>
    </div>
  );
}

function BowlingStats({ career }) {
  const c = career;
  const bestFig = c.best_bowling_wickets > 0 ? `${c.best_bowling_wickets}/${c.best_bowling_runs}` : '-';
  return (
    <div className="space-y-4" data-testid="player-bowling-stats">
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.innings_bowled} label="Inn" color="gray" />
        <StatBadge value={c.total_wickets} label="Wkts" color="navy" />
        <StatBadge value={bestFig} label="Best" color="teal" />
        <StatBadge value={c.bowling_average > 0 ? c.bowling_average.toFixed(1) : '-'} label="Avg" color="pink" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.bowling_economy > 0 ? c.bowling_economy.toFixed(2) : '-'} label="Econ" color="gold" />
        <StatBadge value={c.bowling_strike_rate > 0 ? c.bowling_strike_rate.toFixed(1) : '-'} label="SR" color="gray" />
        <StatBadge value={c.total_maidens} label="Maidens" color="gray" />
        <StatBadge value={c.total_bowling_dots} label="Dots" color="gray" />
      </div>
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={c.five_wicket_hauls} label="5W" color="navy" />
        <StatBadge value={c.three_wicket_hauls} label="3W" color="teal" />
        <StatBadge value={c.bowling_fours_conceded} label="4s Con" color="gray" />
        <StatBadge value={c.bowling_sixes_conceded} label="6s Con" color="pink" />
      </div>
    </div>
  );
}

function FieldingStats({ career }) {
  const c = career;
  const total = c.total_catches + c.total_stumpings + c.total_run_outs;
  return (
    <div className="space-y-4" data-testid="player-fielding-stats">
      <div className="grid grid-cols-4 gap-2">
        <StatBadge value={total} label="Total" color="navy" />
        <StatBadge value={c.total_catches} label="Catches" color="teal" />
        <StatBadge value={c.total_stumpings} label="Stump" color="gold" />
        <StatBadge value={c.total_run_outs} label="Run Outs" color="pink" />
      </div>
    </div>
  );
}

function MatchBreakdown({ matches }) {
  if (!matches?.length) return <p className="text-gray-400 text-sm text-center py-4">No match data available</p>;

  return (
    <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="player-match-breakdown">
      {matches.map((m, idx) => (
        <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
          <div className="flex justify-between items-center mb-1.5">
            <span className="font-bold text-xs text-gray-600">
              vs {m.opponent || 'TBD'}
              {m.match_number && <span className="text-gray-400 ml-1">#{m.match_number}</span>}
            </span>
            {m.is_player_of_match && (
              <span className="bg-[#F4D341] text-[#2B3A67] text-[10px] font-bold px-2 py-0.5 rounded-full">POTM</span>
            )}
          </div>
          <div className="flex gap-4 text-xs text-gray-500">
            {m.batting?.balls > 0 && (
              <span>{m.batting.runs}{m.batting.is_not_out ? '*' : ''} ({m.batting.balls}b)</span>
            )}
            {m.bowling?.overs > 0 && (
              <span>{m.bowling.wickets}/{m.bowling.runs} ({m.bowling.overs}ov)</span>
            )}
            {(m.fielding?.catches > 0 || m.fielding?.stumpings > 0 || m.fielding?.run_outs > 0) && (
              <span>{m.fielding.catches}ct {m.fielding.stumpings}st {m.fielding.run_outs}ro</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Main Component ──

export default function PlayerStatsPanel({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('runs');
  const [leaderboards, setLeaderboards] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerDetail, setPlayerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchLeaderboards = useCallback(async () => {
    try {
      setLoading(true);
      const results = {};
      // Fetch all categories in parallel
      const promises = LEADERBOARD_CATEGORIES.map(async (cat) => {
        try {
          const res = await fetch(`${API_URL}/api/v1/events/${eventId}/player-stats/leaderboard/${cat.key}?limit=10`, { headers });
          if (res.ok) {
            results[cat.key] = await res.json();
          }
        } catch {}
      });
      await Promise.all(promises);
      setLeaderboards(results);
    } catch (err) {
      console.error('Failed to fetch leaderboards:', err);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchLeaderboards();
  }, [fetchLeaderboards]);

  const handlePlayerClick = async (playerPoolId) => {
    try {
      setDetailLoading(true);
      setSelectedPlayer(playerPoolId);
      const res = await fetch(`${API_URL}/api/v1/events/${eventId}/player-stats/${playerPoolId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPlayerDetail(data);
      } else {
        toast.error('Failed to load player details');
        setSelectedPlayer(null);
      }
    } catch (err) {
      toast.error('Failed to load player details');
      setSelectedPlayer(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedPlayer(null);
    setPlayerDetail(null);
  };

  const hasAnyData = Object.values(leaderboards).some(lb => lb?.length > 0);

  if (loading) return <LoadingSpinner />;

  if (!hasAnyData) return <EmptyState message="No player statistics available yet" />;

  return (
    <div className="space-y-5" data-testid="player-stats-panel">
      {/* Player Detail Overlay */}
      {selectedPlayer && (
        <div className="mb-4">
          {detailLoading ? (
            <LoadingSpinner />
          ) : (
            playerDetail && (
              <PlayerDetailCard
                career={playerDetail.career}
                matches={playerDetail.matches}
                onClose={closeDetail}
              />
            )
          )}
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="leaderboard-category-tabs">
        {LEADERBOARD_CATEGORIES.filter(cat => leaderboards[cat.key]?.length > 0).map(cat => (
          <button
            key={cat.key}
            onClick={() => setActiveCategory(cat.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
              activeCategory === cat.key
                ? 'bg-[#2B3A67] text-white shadow-sm'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            data-testid={`leaderboard-tab-${cat.key}`}
          >
            <i className={cat.icon}></i>
            {cat.label}
          </button>
        ))}
      </div>

      {/* Active Leaderboard */}
      <LeaderboardCard
        category={activeCategory}
        entries={leaderboards[activeCategory] || []}
        onPlayerClick={handlePlayerClick}
      />

      {/* Quick Stats Grid (Top 3 categories) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {LEADERBOARD_CATEGORIES
          .filter(cat => cat.key !== activeCategory && leaderboards[cat.key]?.length > 0)
          .slice(0, 4)
          .map(cat => (
            <LeaderboardCard
              key={cat.key}
              category={cat.key}
              entries={(leaderboards[cat.key] || []).slice(0, 5)}
              onPlayerClick={handlePlayerClick}
            />
          ))}
      </div>
    </div>
  );
}
